import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowRight, ShieldCheck, Droplet, Ban, Leaf } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
import { getStorefrontProducts } from '../../../utils/catalog';
import { useCatalogProducts } from '../../../utils/useCatalogProducts';
import { ProductReviews } from './ProductReviews';
import { ProductInfoPanels } from './ProductInfoPanels';
import { CompleteYourStack } from './CompleteYourStack';
export function ProductDetail() {
  const {
    productId
  } = useParams();
  const {
    addItem
  } = useCart();
  const [activeView, setActiveView] = useState<'front' | 'back'>('front');
  const catalogProducts = useCatalogProducts();
  const storefrontProducts = getStorefrontProducts(catalogProducts);
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  const product = storefrontProducts.find(item => item.id === productId);
  const {
    cents
  } = usePrice(product?.lookupKey, product?.price);
  // Scroll to top on mount; reset gallery to the front image per product.
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveView('front');
  }, [productId]);
  if (catalogProducts === null) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-id="element-2807" data-mp="1792qeazxi1i1">
        <span className="w-7 h-7 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" aria-label="Loading product" data-id="element-2808" data-mp="127wygfe3ei9k" />
      </div>;
  }
  if (!product) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center" data-id="element-2809" data-mp="1c6vyxaopf7lh">
        <h1 className="text-3xl font-bold text-[#1F1F1F] mb-4" data-id="element-2810" data-mp="3ixc7bfr07dne">
          Product Not Found
        </h1>
        <p className="text-[#5A5A5A] mb-8" data-id="element-2811" data-mp="3dzae41zv5xy7">
          We couldn't find the supplement you're looking for.
        </p>
        <Link to="/shop" className="px-6 py-3 bg-[#1F1F1F] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors rounded" data-id="element-2812" data-mp="7ijyzms8ws56">
          Back to Shop
        </Link>
      </div>;
  }
  const isBestseller = bestSellers.has(product.id);
  const galleryImages = [{
    key: 'front' as const,
    label: 'Front',
    src: product.image
  }, ...(product.backImage ? [{
    key: 'back' as const,
    label: 'Back',
    src: product.backImage
  }] : [])];
  const activeImage = activeView === 'back' && product.backImage ? product.backImage : product.image;
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="w-full min-h-screen bg-white pt-32 md:pt-36 pb-20" data-id="element-2813" data-mp="2yoj8an6814fb">
      <div className="max-w-7xl mx-auto px-6" data-id="element-2814" data-mp="2tnlq4al7oadt">
        {/* Breadcrumb */}
        <div className="text-sm text-[#5A5A5A] mb-8 md:mb-12" data-id="element-2815" data-mp="2ooil2q58e45u">
          <Link to="/shop" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-2816" data-mp="2op0p48y4xmob">
            Shop All Supplements
          </Link>{' '}
          <span className="mx-2" data-id="element-2817" data-mp="3hfac797fw2mb">/</span>{' '}
          <span className="text-[#1F1F1F] font-medium" data-id="element-2818" data-mp="3cfe7t8uty968">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-2819" data-mp="37muwkijm7q4l">
          {/* LEFT: Image */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col gap-4" data-id="element-2820" data-mp="32ns0hnlm4u8r">
            <div className="relative aspect-square bg-white rounded-2xl border border-[#E5E5E5] flex items-center justify-center p-12 lg:p-20" data-id="element-2821" data-mp="3ftw9ue2owjbn">
              {isBestseller && <span className="absolute top-6 left-6 z-10 px-3 py-1.5 bg-[#1F73B7] text-white text-xs font-bold tracking-wide rounded-sm shadow-sm" data-id="element-2822" data-mp="35ybe2xo7sfac">
                  Best Seller
                </span>}
              <img src={activeImage} alt={`${product.name} — ${activeView} of bottle`} className="w-full h-full object-contain" data-id="element-2823" data-mp="2h2ykkgylris" />
            </div>

            {galleryImages.length > 1 && <div className="flex items-center gap-3 pb-5" role="group" aria-label="Product images" data-id="element-2824" data-mp="3tugwxmfn11ay">
                {galleryImages.map(g => <button key={g.key} type="button" onClick={() => setActiveView(g.key)} aria-pressed={activeView === g.key} aria-label={`View ${g.label} of bottle`} className={`relative w-20 h-20 rounded-lg border-2 bg-white flex items-center justify-center p-2 transition-colors ${activeView === g.key ? 'border-[#1F1F1F]' : 'border-[#E5E5E5] hover:border-[#9A9A90]'}`} data-id="element-2825" data-mp="q6xamztgfc1z">
                    <img src={g.src} alt={`${product.name} ${g.label}`} className="w-full h-full object-contain" data-id="element-2826" data-mp="1ry7zdboor030" />
                    <span className="absolute -bottom-5 left-0 right-0 text-center text-[10px] font-medium uppercase tracking-wider text-[#9A9A90]" data-id="element-2827" data-mp="20ywgif9dlgk4">
                      {g.label}
                    </span>
                  </button>)}
              </div>}
          </motion.div>

          {/* RIGHT: Details */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex flex-col font-shop" data-id="element-2828" data-mp="38e17459vrfzu">
            {/* Title & Rating */}
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F1F1F] mb-4 tracking-tight leading-tight" data-id="element-2829" data-mp="2iyfwsv8tg0tg">
              {product.name}
            </h1>

            <div className="flex items-center gap-1 mb-6" data-id="element-2830" data-mp="1rafj1macaqw7">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-4 h-4 ${star <= Math.round(reviewStats[product.id]?.avg || 0) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2831" data-mp="1va64rbl0egtv" />)}
              <a href="#reviews" className="text-sm text-[#5A5A5A] hover:text-[#1F1F1F] underline decoration-[#E5E5E5] underline-offset-4 ml-2 transition-colors" data-id="element-2832" data-mp="13hp89ge6adni">
                {reviewStats[product.id]?.count || 0} Reviews
              </a>
            </div>

            {/* Description */}
            <p className="text-lg text-[#3B3B3B] leading-relaxed mb-6" data-id="element-2833" data-mp="r0o5sptacmso">
              {product.shortDescription}
            </p>

            <a href="#" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1F73B7] hover:text-[#155A92] transition-colors mb-8" data-id="element-2834" data-mp="m1bdhpjnanip">
              View Ingredient Testing & Quality{' '}
              <ArrowRight className="w-4 h-4" data-id="element-2835" data-mp="7d6oy4xwvhs0" />
            </a>

            {/* Badges */}
            <div className="grid grid-cols-2 gap-y-4 gap-x-6 mb-10" data-id="element-2836" data-mp="7au9pzjjflin">
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2837" data-mp="3d6dmt63zmp6">
                <Ban className="w-5 h-5 text-[#9A9A90]" data-id="element-2838" data-mp="3uglawkclb4k5" />
                No Artificial Sweeteners
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2839" data-mp="3pjlbute5r22j">
                <Droplet className="w-5 h-5 text-[#9A9A90]" data-id="element-2840" data-mp="1han3btpzonk7" />
                No Artificial Coloring
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2841" data-mp="1m6u83j6nft0k">
                <ShieldCheck className="w-5 h-5 text-[#9A9A90]" data-id="element-2842" data-mp="18bjh4gvizs1h" />
                No Artificial Preservatives
              </div>
              <div className="flex items-center gap-3 text-sm text-[#1F1F1F] font-medium" data-id="element-2843" data-mp="yjvv4vhqn37e">
                <Leaf className="w-5 h-5 text-[#9A9A90]" data-id="element-2844" data-mp="13ac8kflk6otr" />
                No Fillers
              </div>
            </div>

            {/* Serving Size */}
            <div className="mb-8" data-id="element-2845" data-mp="pkiixv28c0iz">
              <p className="text-sm text-[#5A5A5A] mb-2" data-id="element-2846" data-mp="tkiwldhi87oz">Serving Size:</p>
              <div className="inline-flex items-center justify-center px-6 py-3 border border-[#1F1F1F] rounded text-sm font-bold text-[#1F1F1F]" data-id="element-2847" data-mp="tksmz26gogv7">
                30 Servings
              </div>
            </div>

            {/* Purchase Panel */}
            <div className="bg-[#F9F9F9] border border-[#E5E5E5] rounded-xl p-6 mb-8" data-id="element-2848" data-mp="xj8kf9f0s0l">
              <div className="flex items-center justify-between mb-6" data-id="element-2849" data-mp="3s4lx0yxvck74">
                <div className="flex items-center gap-3" data-id="element-2850" data-mp="3s6gn0rnzz2i2">
                  <div className="w-5 h-5 rounded-full border-4 border-[#1F1F1F] bg-white" data-id="element-2851" data-mp="axa0c96lcswz" />
                  <span className="text-base font-bold text-[#1F1F1F]" data-id="element-2852" data-mp="5w48edkr99oi">
                    One-Time Purchase
                  </span>
                </div>
                <span className="text-xl font-bold text-[#1F1F1F]" data-id="element-2853" data-mp="jukf44imb06n">
                  {cents !== null ? `$${(cents / 100).toFixed(2)}` : '—'}
                </span>
              </div>

              <button onClick={handleAdd} disabled={cents === null || !product.lookupKey} className="w-full py-4 bg-[#1F1F1F] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors disabled:opacity-40 rounded shadow-sm" data-id="element-2854" data-mp="tot3cg0m86z0">
                Add to Cart
              </button>
            </div>

            {/* Accordion Details */}
            <details className="group border-t border-[#E5E5E5]" data-id="element-2855" data-mp="3e8m71i6e2pms">
              <summary className="cursor-pointer list-none flex items-center justify-between py-5 text-base font-bold text-[#1F1F1F]" data-id="element-2856" data-mp="3e718zikv8ok6">
                Why this dose?
                <span className="text-2xl font-light leading-none group-open:rotate-45 transition-transform origin-center" data-id="element-2857" data-mp="34b7qlikgu0uh">
                  +
                </span>
              </summary>
              <p className="text-base text-[#5A5A5A] leading-relaxed pb-6" data-id="element-2858" data-mp="t6a6l0xnhsp">
                {product.whyEffective}
              </p>
            </details>
            <details className="group border-t border-b border-[#E5E5E5]" data-id="element-2859" data-mp="3s1lldgufqdsr">
              <summary className="cursor-pointer list-none flex items-center justify-between py-5 text-base font-bold text-[#1F1F1F]" data-id="element-2860" data-mp="3nxvziz55aabf">
                How to use
                <span className="text-2xl font-light leading-none group-open:rotate-45 transition-transform origin-center" data-id="element-2861" data-mp="2ciqtpm9pvvnl">
                  +
                </span>
              </summary>
              <p className="text-base text-[#5A5A5A] leading-relaxed pb-6" data-id="element-2862" data-mp="w21m9bp0sdui">
                Take {product.dose} {product.doseUnit} daily for optimal
                results. Consistency is key.
              </p>
            </details>
          </motion.div>
        </div>
      </div>

      <ProductInfoPanels product={product} data-id="element-2863" data-mp="1arfuuhkrur8u" />

      <CompleteYourStack product={product} products={storefrontProducts} data-id="element-2864" data-mp="1aqnxp2bj0lgg" />

      <ProductReviews productId={product.id} data-id="element-2865" data-mp="1fpr5guiw55s4" />
    </div>;
}